import React, { lazy, Suspense } from 'react'
import type { RouteObject } from 'react-router-dom'
import { LandingPage } from '../pages/LandingPage'
import { LoginPage } from '../pages/LoginPage'
import { NotFoundPage } from '../pages/NotFoundPage'
import { ProtectedRoute } from './ProtectedRoute'
import { Loading } from '../components/ui/Loading'
import { RouteErrorBoundary } from '../components/shared/RouteErrorBoundary'

// Lazy-loaded page components for code splitting
const ContentManagerPage = lazy(() =>
	import('../pages/ContentManagerPage').then((m) => ({ default: m.ContentManagerPage }))
)
const PlaybookEditorPage = lazy(() =>
	import('../pages/PlaybookEditorPage').then((m) => ({ default: m.PlaybookEditorPage }))
)
const DocumentEditorPage = lazy(() =>
	import('../pages/DocumentEditorPage').then((m) => ({ default: m.DocumentEditorPage }))
)
const PlayEditorPage = lazy(() =>
	import('../pages/PlayEditorPage').then((m) => ({ default: m.PlayEditorPage }))
)
const PlayAnimationPage = lazy(() =>
	import('../pages/PlayAnimationPage').then((m) => ({ default: m.PlayAnimationPage }))
)
const TeamAdminPage = lazy(() =>
	import('../pages/TeamAdminPage').then((m) => ({ default: m.TeamAdminPage }))
)
const ConceptEditorPage = lazy(() =>
	import('../pages/ConceptEditorPage').then((m) => ({ default: m.ConceptEditorPage }))
)
const FormationEditorPage = lazy(() =>
	import('../pages/FormationEditorPage').then((m) => ({ default: m.FormationEditorPage }))
)

/**
 * Wraps a lazy-loaded component with Suspense and Loading fallback
 */
function withSuspense(Component: React.ComponentType) {
	return (
		<Suspense fallback={<Loading fullscreen text="Loading page..." />}>
			<Component />
		</Suspense>
	)
}

export const routes: RouteObject[] = [
	{
		path: '/',
		element: <LandingPage />,
		errorElement: <RouteErrorBoundary />,
	},
	{
		path: '/login',
		element: <LoginPage />,
		errorElement: <RouteErrorBoundary />,
	},
	{
		path: '/content',
		element: <ProtectedRoute>{withSuspense(ContentManagerPage)}</ProtectedRoute>,
		errorElement: <RouteErrorBoundary />,
	},
	{
		path: '/playbooks/:playbookId',
		element: <ProtectedRoute>{withSuspense(PlaybookEditorPage)}</ProtectedRoute>,
		errorElement: <RouteErrorBoundary />,
	},
	{
		path: '/playbooks/:playbookId/play/:playId',
		element: <ProtectedRoute>{withSuspense(PlayEditorPage)}</ProtectedRoute>,
		errorElement: <RouteErrorBoundary />,
	},
	{
		path: '/playbooks/:playbookId/animate/:playId',
		element: <ProtectedRoute>{withSuspense(PlayAnimationPage)}</ProtectedRoute>,
		errorElement: <RouteErrorBoundary />,
	},
	{
		path: '/documents/:documentId/edit',
		element: <ProtectedRoute>{withSuspense(DocumentEditorPage)}</ProtectedRoute>,
		errorElement: <RouteErrorBoundary />,
	},
	{
		path: '/teams/:teamId/admin',
		element: <ProtectedRoute>{withSuspense(TeamAdminPage)}</ProtectedRoute>,
		errorElement: <RouteErrorBoundary />,
	},
	{
		path: '/playbooks/:playbookId/formation/:formationId',
		element: <ProtectedRoute>{withSuspense(FormationEditorPage)}</ProtectedRoute>,
		errorElement: <RouteErrorBoundary />,
	},
	{
		path: '/playbooks/:playbookId/concept/:conceptId',
		element: <ProtectedRoute>{withSuspense(ConceptEditorPage)}</ProtectedRoute>,
		errorElement: <RouteErrorBoundary />,
	},
	{
		path: '*',
		element: <NotFoundPage />,
		errorElement: <RouteErrorBoundary />,
	},
]
